import { IStringDictionary } from "Core/Interfaces";
import { Util } from "Core";

/**
 * ## Query String Parameters
 *
 * Get access to parameters passed in the querystring.
 * @attention All parameter keys are case insensitive.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // get a query parameter from uri ../DMS/Test?param1=test
 * var param = DMS.QueryParameter.get('param1');
 * ```
 */
export class QueryParameter {
    private static _queryParameters: IStringDictionary<string> = {};

    /**
     * Get all query parameters as dictionary.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // get all query parameter from uri ../DMS/Test?param1=test&param2=test2
     * var params = DMS.QueryParameter.getAll();
     * alert(params['param1']); // outputs test
     * ```
     */
    public static getAll(): IStringDictionary<string> {
        return this._queryParameters;
    }

    /**
     * Get the value of a single query parameter.
     * @param name Name of the parameter.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // get query parameter from uri ../DMS/Test?param1=test&param2=test2
     * var param = DMS.QueryParameter.get('param1');
     * alert(param); // outputs test
     * ```
     */
    public static get(name: string): string {
        let key = name.toLowerCase();
        return this._queryParameters[key] != null ? this._queryParameters[key] : null;
    }

    /**
     * @hidden
     */
    public static clear(): void {
        this._queryParameters = {};
    }

    /**
     * Initialize query parameters.
     * @hidden
     */
    public static initialize(): void {
        this.addFromQueryString(window.location.search);
    }

    /**
     * Add single query string parameter. For internal use only.
     * @hidden
     */
    public static add(key: string, value: string): void {
        this._queryParameters[key] = value;
    }

    /**
     * Add parameters from query string.
     * @param query Querystring to be parsed.
     * @hidden
     */
    public static addFromQueryString(query: string, clearExisting: boolean = true) {
        if (!query) {
            return;
        }
        if (clearExisting) {
            this._queryParameters = {};
        }

        if (query.length > 0 && query.charAt(0) === "?") {
            query = query.substr(1);
        }
        let lets = query.split("&");
        for (let i = 0; i < lets.length; i++) {
            let pair = lets[i].split("=");
            try {
                let value = decodeURIComponent(pair[1]);
                this._queryParameters[decodeURIComponent(pair[0]).toLowerCase()] = Util.stringIsNullOrEmpty(value) ? null : value;
            } catch (e) {
                alert("Invalid QueryParametervalue: " + lets[i]);
            }
        }
    }

    /**
     * Add parameters from object.
     * @param object Object with parameters.
     * @hidden
     */
    public static addFromObject(object: Object) {
        let keys = Object.keys(object);
        for (let i = 0, cnt = keys.length; i < cnt; i++) {
            let key = keys[i];
            this._queryParameters[key] = object[key];
        }
    }
}
